import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useFormik } from 'formik';
import { translatedMessage } from '../../service/LanguageService';
import { Dropdown } from 'primereact/dropdown';
import { classNames } from 'primereact/utils';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { CompetitionService } from '../../service/CompetitionService';
import GeneralUtils from '../../utilities/GeneralUtils';
import { InputTextarea } from 'primereact/inputtextarea';
import EnumService from '../../service/EnumService';
import { InputText } from 'primereact/inputtext';

const defaulNewValue = {
    id: null,
    language: null,
    level: 'A1',
    proofingMethod: '',
    languageName: ''
}

const CompetitionLanguageEditDialog = (props) => {
    const [competitionLanguage, setCompetitionLanguage] = useState(null);
    const [languageList, setLanguageList] = useState([]);
    const [languageLevels, setLanguageLevels] = useState([]);
    const toast = useRef(null);

    const competitionService = useMemo(() => new CompetitionService(), []);

    useEffect(() => {
        // console.log("props: ", props)
        const getLanguages = async () => {
            const languagesResponse = await EnumService.getEntityLookupByField("NOM:LANGUAGE")
            // console.log("getLanguages: ", languagesResponse)
            setLanguageList(languagesResponse)
            return languagesResponse;
        }

        const getLevels = async () => {
            setLanguageLevels(await EnumService.getEnumByName('LanguageLevel'));
        };

        const computeLanguage = async () => {
            let _languages = await getLanguages();
            getLevels();
            let _competitionLanguage = props.language ? props.language : { ...defaulNewValue, competition: { id: props?.competition?.id } }
            if (_competitionLanguage?.id) {
                let _language = _languages.filter(item => item.value === _competitionLanguage?.language?.id)
                _competitionLanguage = { ..._competitionLanguage, language: _language[0] ? _language[0] : null }
            }

            setCompetitionLanguage(_competitionLanguage);
        };


        computeLanguage()

    }, [props]);

    // Form
    const formik = useFormik({
        initialValues: competitionLanguage ? competitionLanguage : { ...defaulNewValue, competition: { id: props?.competition?.id } },
        enableReinitialize: true,
        validate: (data) => {
            let errors = {};

            // console.log("validate: ", data)

            if (props?.minorityLanguages) {
                if (!data.languageName || data.languageName.trim() === '') {
                    errors.languageName = translatedMessage("form.error.language.language.required");
                }
            } else {
                if (!data.language || !data.language?.value) {
                    errors.language = translatedMessage("form.error.language.language.required");
                }
            }

            if (!data.proofingMethod || data.proofingMethod.trim() === '') {
                errors.proofingMethod = translatedMessage('form.error.competition.language.proofingMethod.required');
            }


            return errors;
        },
        onSubmit: (data) => {
            let _data = { ...GeneralUtils.nullIfEmptyForObjectFields(data) };
            _data.language = props?.minorityLanguages ? null : { id: data.language.value }
            _data.competition = { id: data.competition.id }
            _data.minorityLanguage = props?.minorityLanguages

            // console.log("saved data: ", _data)

            competitionService.saveCompetitionLanguage(_data)
                .then(savedLanguage => {
                    if (typeof props.afterSave === 'function') {
                        props.afterSave(savedLanguage, true);
                    }
                    handleDialogClose()
                })
                .catch(error => {
                    if (typeof props.afterSave === 'function') {
                        props.afterSave(null, false, error);
                    }
                });
        }
    });

    const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);
    const getFormErrorMessage = (name) => {
        return isFormFieldValid(name) && <small className="p-error text-align-left">{formik.errors[name]}</small>;
    };

    const dialogFooter = (
        <>
            <Button label={translatedMessage("generic.cancel")} icon="pi pi-times" className="p-button-text" onClick={() => handleDialogClose()} />
            <Button label={translatedMessage("generic.save")} icon="pi pi-check" className="p-button-text" form="language-form" type="submit" />
        </>
    )

    const handleDialogClose = () => {
        props.onClose()
        formik.resetForm()
    }

    const handleLanguageChange = (data, resetValues = true) => {
        // console.log("handleLanguageChange: ", data.value)

        let _language = languageList.filter(item => item.value === data.value)
        formik.setFieldValue("language", _language[0] ? _language[0] : {})
    }

    return (
        <>
            <Toast ref={toast} />
            <Dialog
                visible={props.visible}
                closable={true}
                onHide={handleDialogClose}
                header={translatedMessage("competition.requirements.language")}
                modal
                className="pcn-dialog p-fluid"
                footer={dialogFooter}
            >
                <form id="language-form" onSubmit={formik.handleSubmit}>
                    <div className="input-panel flex flex-column mb-5">
                        {!props?.minorityLanguages && <>
                            <div className="p-inputgroup mt-3">
                                <span className="p-float-label">
                                    <Dropdown id="language" name="language" value={formik.values.language && formik.values.language.value}
                                        options={languageList} onChange={handleLanguageChange} optionLabel="label"
                                        className={classNames({ 'p-invalid': isFormFieldValid('language') })} />

                                    <label htmlFor="language" className={classNames({ 'p-error': isFormFieldValid('language') })}>
                                        {translatedMessage("generic.language")}
                                    </label>
                                </span>
                            </div>
                            {getFormErrorMessage('language')}
                        </>}

                        {props?.minorityLanguages && <>
                            <div className="p-inputgroup mt-3">
                                <span className="p-float-label">
                                    <InputText type="text" name="languageName" id="languageName"
                                        value={formik.values?.languageName} onChange={formik.handleChange}
                                        className={classNames({ 'p-invalid': isFormFieldValid('languageName') })} />
                                    <label htmlFor="languageName" className={classNames({ 'p-error': isFormFieldValid('languageName') })}>
                                        {translatedMessage("generic.language")} *
                                    </label>
                                </span>
                            </div>
                            {getFormErrorMessage('languageName')}
                        </>}

                        <div className="p-inputgroup mt-3">
                            <span className="p-float-label">
                                <Dropdown id="level" name="level" value={formik.values.level}
                                    options={languageLevels} onChange={formik.handleChange}
                                    className={classNames({ 'p-invalid': isFormFieldValid('level') })} />

                                <label htmlFor="level" className={classNames({ 'p-error': isFormFieldValid('level') })}>
                                    {translatedMessage("competition.requirements.language.level")} *
                                </label>
                            </span>
                        </div>
                        {getFormErrorMessage('level')}

                        <div className="p-inputgroup mt-3">
                            <span className="p-float-label">
                                <InputTextarea type="text" id="proofingMethod" name="proofingMethod" value={formik.values?.proofingMethod}
                                    onChange={formik.handleChange} className={classNames({ 'p-invalid': isFormFieldValid('proofingMethod') })}
                                    rows={5} cols={30} autoResize
                                    maxLength={10000}
                                    tooltip={translatedMessage("field.character.maxLength", "10.000")}
                                    tooltipOptions={{ position: 'top' }} />
                                <label htmlFor="proofingMethod" className={classNames({ 'p-error': isFormFieldValid('proofingMethod') })}>
                                    {translatedMessage("competition.requirements.language.proofing")} *
                                </label>
                            </span>
                        </div>
                        {getFormErrorMessage('proofingMethod')}
                    </div>
                </form>
            </Dialog>
        </>
    );
};

export default CompetitionLanguageEditDialog;
